import { Helmet } from 'react-helmet-async';

interface OrganizationSchemaProps {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  contactPoint?: {
    telephone?: string;
    contactType?: string;
    email?: string;
  };
  sameAs?: string[];
}

interface PersonSchemaProps {
  name: string;
  description?: string;
  image?: string;
  url?: string;
  jobTitle?: string;
  address?: {
    addressLocality?: string;
    addressCountry?: string;
    streetAddress?: string;
  };
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
  };
  aggregateRating?: {
    ratingValue?: number;
    reviewCount?: number;
  };
}

interface RecipeSchemaProps {
  name: string;
  description: string;
  image?: string;
  url?: string;
  author: {
    name: string;
    url?: string;
  };
  datePublished?: string;
  dateModified?: string;
  prepTime?: string;
  cookTime?: string;
  totalTime?: string;
  recipeYield?: string;
  recipeCategory?: string;
  recipeCuisine?: string;
  keywords?: string[];
  recipeIngredient: string[];
  recipeInstructions: Array<{
    '@type': string;
    text: string;
    name?: string;
    position?: number;
    url?: string;
    image?: string;
  }>;
  nutrition?: {
    calories?: string;
    servingSize?: string;
  };
  aggregateRating?: {
    ratingValue: number;
    ratingCount: number;
  };
}

interface CourseSchemaProps {
  name: string;
  description: string;
  image?: string;
  provider: {
    name: string;
    url?: string;
  };
  courseCode?: string;
  educationalCredentialAwarded?: string;
  timeRequired?: string;
  numberOfCredits?: number;
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
  };
  aggregateRating?: {
    ratingValue?: number;
    reviewCount?: number;
  };
}

interface EventSchemaProps {
  name: string;
  description: string;
  image?: string;
  startDate: string;
  endDate?: string;
  location: {
    name?: string;
    address?: {
      streetAddress?: string;
      addressLocality?: string;
      addressRegion?: string;
      postalCode?: string;
      addressCountry?: string;
    };
  };
  organizer?: {
    name: string;
    url?: string;
  };
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
    url?: string;
  };
}

interface PlaceSchemaProps {
  name: string;
  description: string;
  image?: string;
  address: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  telephone?: string;
  priceRange?: string;
  openingHours?: string;
}

interface JobPostingSchemaProps {
  title: string;
  description: string;
  hiringOrganization: {
    name: string;
    logo?: string;
  };
  jobLocation: {
    address: {
      addressLocality?: string;
      addressRegion?: string;
      addressCountry?: string;
    };
  };
  employmentType?: string;
  baseSalary?: {
    currency?: string;
    value?: {
      minValue?: number;
      maxValue?: number;
    };
  };
  datePosted?: string;
  validThrough?: string;
}

interface ArticleSchemaProps {
  headline: string;
  description?: string;
  image?: string;
  url?: string;
  author: {
    name: string;
    url?: string;
  };
  datePublished: string;
  dateModified?: string;
  publisher: {
    name: string;
    logo?: string;
  };
}

type SchemaProps =
  | { type: 'Organization'; data: OrganizationSchemaProps }
  | { type: 'Person'; data: PersonSchemaProps }
  | { type: 'Recipe'; data: RecipeSchemaProps }
  | { type: 'Course'; data: CourseSchemaProps }
  | { type: 'Event'; data: EventSchemaProps }
  | { type: 'Place'; data: PlaceSchemaProps }
  | { type: 'JobPosting'; data: JobPostingSchemaProps }
  | { type: 'Article'; data: ArticleSchemaProps };

export function Schema({ type, data }: SchemaProps) {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mindful.family';
  
  let schema: any = {
    '@context': 'https://schema.org',
    '@type': type,
  };

  switch (type) {
    case 'Organization':
      schema = {
        ...schema,
        name: data.name || 'Mindful Family',
        url: data.url || baseUrl,
        logo: data.logo || `${baseUrl}/android-chrome-512x512.png`,
        description: data.description || 'A global platform where wellness communities, holistic health practitioners, and eco-conscious enthusiasts unite to promote mindful living.',
        ...(data.contactPoint && {
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: data.contactPoint.telephone,
            contactType: data.contactPoint.contactType || 'Customer Service',
            email: data.contactPoint.email || 'support@mindful.family',
          },
        }),
        ...(data.sameAs && { sameAs: data.sameAs }),
      };
      break;

    case 'Person':
      schema = {
        ...schema,
        name: data.name,
        description: data.description,
        ...(data.image && { image: data.image }),
        ...(data.url && { url: data.url }),
        ...(data.jobTitle && { jobTitle: data.jobTitle }),
        ...(data.address && {
          address: {
            '@type': 'PostalAddress',
            ...data.address,
          },
        }),
        ...(data.offers && {
          offers: {
            '@type': 'Offer',
            ...data.offers,
          },
        }),
        ...(data.aggregateRating && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ...data.aggregateRating,
          },
        }),
      };
      break;

    case 'Recipe':
      schema = {
        ...schema,
        name: data.name,
        description: data.description,
        ...(data.image && { image: data.image }),
        ...(data.url && { url: data.url }),
        author: {
          '@type': 'Person',
          name: data.author.name,
          ...(data.author.url && { url: data.author.url }),
        },
        publisher: {
          '@type': 'Organization',
          name: 'Mindful Family',
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/android-chrome-512x512.png`,
          },
        },
        ...(data.datePublished && { datePublished: data.datePublished }),
        ...(data.dateModified && { dateModified: data.dateModified }),
        ...(data.prepTime && { prepTime: `PT${data.prepTime}M` }),
        ...(data.cookTime && { cookTime: `PT${data.cookTime}M` }),
        ...(data.totalTime && { totalTime: `PT${data.totalTime}M` }),
        ...(data.recipeYield && { recipeYield: data.recipeYield }),
        ...(data.recipeCategory && { recipeCategory: data.recipeCategory }),
        ...(data.recipeCuisine && { recipeCuisine: data.recipeCuisine }),
        ...(data.keywords && data.keywords.length > 0 && { keywords: data.keywords.join(', ') }),
        recipeIngredient: data.recipeIngredient,
        recipeInstructions: data.recipeInstructions.map((instruction, index) => ({
          '@type': 'HowToStep',
          position: instruction.position || index + 1,
          text: instruction.text,
          name: instruction.name || `Step ${index + 1}`,
          url: instruction.url || (data.url ? `${data.url}#step-${index + 1}` : undefined),
          ...(instruction.image && { image: instruction.image }),
        })),
        // Nutrition info (recommended by Google)
        nutrition: {
          '@type': 'NutritionInformation',
          ...(data.nutrition?.calories && { calories: data.nutrition.calories }),
          ...(data.nutrition?.servingSize && { servingSize: data.nutrition.servingSize }),
        },
        // Aggregate rating (recommended by Google)
        ...(data.aggregateRating && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: data.aggregateRating.ratingValue,
            ratingCount: data.aggregateRating.ratingCount,
          },
        }),
      };
      break;

    case 'Course':
      schema = {
        ...schema,
        name: data.name,
        description: data.description,
        ...(data.image && { image: data.image }),
        provider: {
          '@type': 'Organization',
          name: data.provider.name,
          ...(data.provider.url && { url: data.provider.url }),
        },
        ...(data.courseCode && { courseCode: data.courseCode }),
        ...(data.educationalCredentialAwarded && { educationalCredentialAwarded: data.educationalCredentialAwarded }),
        ...(data.timeRequired && { timeRequired: `PT${data.timeRequired}M` }),
        ...(data.numberOfCredits && { numberOfCredits: data.numberOfCredits }),
        ...(data.offers && {
          offers: {
            '@type': 'Offer',
            ...data.offers,
          },
        }),
        ...(data.aggregateRating && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ...data.aggregateRating,
          },
        }),
      };
      break;

    case 'Event':
      schema = {
        ...schema,
        name: data.name,
        description: data.description,
        ...(data.image && { image: data.image }),
        startDate: data.startDate,
        ...(data.endDate && { endDate: data.endDate }),
        location: {
          '@type': 'Place',
          name: data.location.name,
          ...(data.location.address && {
            address: {
              '@type': 'PostalAddress',
              ...data.location.address,
            },
          }),
        },
        ...(data.organizer && {
          organizer: {
            '@type': 'Organization',
            name: data.organizer.name,
            ...(data.organizer.url && { url: data.organizer.url }),
          },
        }),
        ...(data.offers && {
          offers: {
            '@type': 'Offer',
            ...data.offers,
          },
        }),
      };
      break;

    case 'Place':
      schema = {
        ...schema,
        name: data.name,
        description: data.description,
        ...(data.image && { image: data.image }),
        address: {
          '@type': 'PostalAddress',
          ...data.address,
        },
        ...(data.telephone && { telephone: data.telephone }),
        ...(data.priceRange && { priceRange: data.priceRange }),
        ...(data.openingHours && { openingHours: data.openingHours }),
      };
      break;

    case 'JobPosting':
      schema = {
        ...schema,
        title: data.title,
        description: data.description,
        hiringOrganization: {
          '@type': 'Organization',
          name: data.hiringOrganization.name,
          ...(data.hiringOrganization.logo && { logo: data.hiringOrganization.logo }),
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            ...data.jobLocation.address,
          },
        },
        ...(data.employmentType && { employmentType: data.employmentType }),
        ...(data.baseSalary && {
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: data.baseSalary.currency,
            value: {
              '@type': 'QuantitativeValue',
              ...data.baseSalary.value,
            },
          },
        }),
        ...(data.datePosted && { datePosted: data.datePosted }),
        ...(data.validThrough && { validThrough: data.validThrough }),
      };
      break;

    case 'Article':
      schema = {
        ...schema,
        headline: data.headline,
        ...(data.description && { description: data.description }),
        ...(data.image && { image: data.image }),
        ...(data.url && { url: data.url }),
        author: {
          '@type': 'Person',
          name: data.author.name,
          ...(data.author.url && { url: data.author.url }),
        },
        datePublished: data.datePublished,
        ...(data.dateModified && { dateModified: data.dateModified }),
        publisher: {
          '@type': 'Organization',
          name: data.publisher.name,
          ...(data.publisher.logo && { logo: data.publisher.logo }),
        },
      };
      break;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

